import { CheckCircle, CircleOutlined, Search } from '@mui/icons-material';
import { Checkbox, InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import Code from '@/components/Modal/Project/Code';
import Loader from '@/components/ui/Loader/Loader';
import Delete from '@/components/Modal/General/Delete';

import usePatch from '@/hooks/usePatch';
import project from '@/api/project';
import icAddNew from '@/assets/icons/add-new-user.png';

const { getProductPrefix } = project();

export default function Codes() {
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [newCode, setNewCode] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [deleteCode, setDeleteCode] = useState(false);

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['codes', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProductPrefix(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length;
			if (isFetched && count) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]) || [];

	const onChangeValidate = (e) => {
		const val = e.target.checked;
		if (val) return;
	};

	const {
		mutate: deleteCodes,
		isPending,
		isSuccess,
	} = usePatch({
		url: `productprefix/${deleteCode?.id}`,
		method: 'delete',
		callback: () => {
			toast.success(`kode deleted`);
			refetch();
			setDeleteCode(false)
		},
		onError: () => {},
		showError: true,
	});

	const onDeleteCode = () => {
		if (!deleteCode) return;
		deleteCodes();
	};

	const columns = [
		{
			field: '',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			width: 64,
			renderCell: (index) => {
				const pM = paginationModel;
				const idx = index.api.getAllRowIds().indexOf(index.id) + 1;
				return idx + pM.page * pM.pageSize;
			},
		},
		{
			field: 'productprefix',
			headerName: 'Kodenavn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'icon',
			headerName: 'Icon',
			headerAlign: 'center',
			align: 'center',
			width: 96,
			renderCell: (params) => {
				const { icon, productprefix } = params.row;
				return icon === 'No icon' ? (
					'-'
				) : (
					<div className='flex object-contain w-full h-full m-auto'>
						<img src={icon} alt={productprefix} className='w-auto h-12 py-2 m-auto' />
					</div>
				);
			},
		},
		{
			field: 'amount_of_use',
			headerName: 'Kode anvendt',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'validate',
			headerName: 'Valider filenavn',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (row) => {
				return (
					<Checkbox
						onChange={(e) => onChangeValidate('update', e)}
						defaultChecked={row.row?.validate === 0}
						icon={<CircleOutlined />}
						checkedIcon={<CheckCircle />}
					/>
				);
			},
		},
		{
			field: 'action',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (d) => {
				const { amount_of_use, productprefixid, productprefix } = d.row;
				const isDisable = amount_of_use > 0;
				return (
					<button
						disabled={isDisable}
						onClick={() => !isDisable && setDeleteCode({ id: productprefixid, name: productprefix })}
						className={twMerge(isDisable ? 'opacity-30' : '')}
					>
						<Trash2 size={16} />
					</button>
				);
			},
		},
	];

	const onSaveCode = () => {
		setSelectedRow(false);
		setNewCode(false);
		refetch();
	};
	return (
		<>
			<BDHelmet title='Codes' description='BDS Samferdsel project codes' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk i dokument her ...'
							onChange={(e) => {
								setSearch(e.target.value);
								if (paginationModel.page !== 0) {
									setPaginationModel({ page: 0, pageSize: 10 });
								}
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewCode(true)}
							title='Registrer ny bruker'
							className='relative flex items-center gap-2 min-w-[124px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Ny kode
						</button>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div className='mx-auto'>
						{isPending && !isSuccess && <Loader />}

						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							className='h-full mx-auto mt-0 w-[780px]'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							getRowId={(row) => row.productprefixid}
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								cellClass += ' cell-fix';
								if (selectedRow?.productprefixid === params.row.productprefixid) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
							pageSizeOptions={[10, 20, 50, 99]}
						/>
					</div>
				</div>
				{!!selectedRow && <Code open={!!selectedRow} onSave={onSaveCode} data={selectedRow} onClose={() => setSelectedRow(false)} />}
				{!!newCode && <Code open={!!newCode} onSave={onSaveCode} onClose={() => setNewCode(false)} />}
				{!!deleteCode && <Delete open={!!deleteCode} data={deleteCode} onSave={onDeleteCode} onClose={() => setDeleteCode(false)} />}
			</div>
		</>
	);
}
